import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { useAuth } from "@/lib/authContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Save, Eye, Plus, Trash2, ArrowUp, ArrowDown,
    ShieldAlert, Info,
} from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import CatalogSelector from "@/components/CatalogSelector";
import ReturnToWorkspace from "@/components/ReturnToWorkspace";

const SOCIAL_PLATFORMS = ["Instagram", "TikTok", "YouTube", "Facebook", "X / Twitter", "Other"];
const MUSIC_PLATFORMS = ["Spotify", "Apple Music", "YouTube Music", "SoundCloud", "Audiomack", "Bandcamp", "Other"];
const RELEASE_TYPES = ["album", "ep", "single"];
const MERCH_TYPES = ["Apparel", "Vinyl", "CD", "Cassette", "Poster", "Print", "Accessory", "Other"];

const EDIT_ALLOWED_ROLES = new Set(["artist_owner", "artist_team", "admin"]);

function isValidUrl(u) {
    if (!u) return false;
    try {
        const parsed = new URL(u);
        return (parsed.protocol === "http:" || parsed.protocol === "https:") && !!parsed.host;
    } catch { return false; }
}
function tempId() { return "tmp_" + Math.random().toString(36).slice(2, 10); }

export default function ArtistPageEditor() {
    const { activeArtistId, role } = useRole();
    const navigate = useNavigate();
    const canEdit = EDIT_ALLOWED_ROLES.has(role);

    const [artist, setArtist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        name: "", image_url: "", cover_url: "", bio: "", genre: "", additional_genres: [], city: "", country: "",
        website: "", shopify_url: "", social_links: [], music_links: [], albums: [], merch: [],
    });
    const [status, setStatus] = useState(null); // onboarding-status: visibility, can_publish, checklist
    const [publishing, setPublishing] = useState(false);

    const loadStatus = () => {
        if (!activeArtistId) return;
        endpoints.onboardingStatus(activeArtistId).then(setStatus).catch(() => setStatus(null));
    };

    const load = () => {
        if (!activeArtistId) return;
        setLoading(true);
        endpoints.getArtist(activeArtistId).then((d) => {
            setArtist(d.artist);
            setForm({
                name: d.artist.name || "",
                image_url: d.artist.image_url || "",
                cover_url: d.artist.cover_url || "",
                bio: d.artist.bio || "",
                genre: d.artist.genre || "",
                additional_genres: d.artist.additional_genres || [],
                city: d.artist.city || "",
                country: d.artist.country || "",
                website: d.artist.website || "",
                shopify_url: d.artist.shopify_url || "",
                social_links: (d.artist.social_links || []).map((l) => ({ ...l, id: l.id || tempId() })),
                music_links: (d.artist.music_links || []).map((l) => ({ ...l, id: l.id || tempId() })),
                albums: (d.artist.albums || []).map((al) => ({
                    ...al, id: al.id || tempId(),
                    release_type: al.release_type || al.type || "album",
                    external_url: al.external_url || "",
                    description: al.description || "",
                })),
                merch: (d.artist.merch || []).map((m) => ({
                    ...m, id: m.id || tempId(),
                    active: m.active !== false,
                    product_type: m.product_type || "Apparel",
                })),
            });
            setLoading(false);
        }).catch(() => setLoading(false));
    };
    useEffect(load, [activeArtistId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadStatus, [activeArtistId]);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((prev) => { if (!prev[k]) return prev; const { [k]: _, ...rest } = prev; return rest; });
    };
    const listAdd = (key, item) => setForm((f) => ({ ...f, [key]: [...f[key], { id: tempId(), ...item }] }));
    const listUpdate = (key, id, patch) => setForm((f) => ({ ...f, [key]: f[key].map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
    const listRemove = (key, id) => setForm((f) => ({ ...f, [key]: f[key].filter((x) => x.id !== id) }));
    const listMove = (key, id, dir) => setForm((f) => {
        const items = [...f[key]];
        const idx = items.findIndex((x) => x.id === id);
        const next = idx + dir;
        if (idx < 0 || next < 0 || next >= items.length) return f;
        [items[idx], items[next]] = [items[next], items[idx]];
        return { ...f, [key]: items };
    });

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Artist name is required.";
        if (!form.genre.trim()) errs.genre = "Genre is required.";
        if (!form.city.trim()) errs.city = "City is required.";
        if (!form.country.trim()) errs.country = "Country is required.";
        if (!form.bio.trim()) errs.bio = "Biography is required.";
        if (form.website && !isValidUrl(form.website)) errs.website = "Enter a valid URL (https://…).";
        if (form.shopify_url && !isValidUrl(form.shopify_url)) errs.shopify_url = "Enter a valid URL (https://…).";
        const socialErrs = {};
        form.social_links.forEach((l) => {
            const e = {};
            if (!l.platform) e.platform = "Platform required.";
            if (!isValidUrl(l.url)) e.url = "Enter a valid URL.";
            if (Object.keys(e).length) socialErrs[l.id] = e;
        });
        if (Object.keys(socialErrs).length) errs.social_links = socialErrs;
        const musicErrs = {};
        form.music_links.forEach((l) => {
            const e = {};
            if (!l.platform) e.platform = "Platform required.";
            if (!isValidUrl(l.url)) e.url = "Enter a valid URL.";
            if (Object.keys(e).length) musicErrs[l.id] = e;
        });
        if (Object.keys(musicErrs).length) errs.music_links = musicErrs;
        const albumErrs = {};
        form.albums.forEach((al) => {
            const e = {};
            if (!al.title || !al.title.trim()) e.title = "Title required.";
            if (al.external_url && !isValidUrl(al.external_url)) e.external_url = "Enter a valid URL.";
            if (Object.keys(e).length) albumErrs[al.id] = e;
        });
        if (Object.keys(albumErrs).length) errs.albums = albumErrs;
        const merchErrs = {};
        form.merch.forEach((m) => {
            const e = {};
            if (!m.title || !m.title.trim()) e.title = "Product name required.";
            if (!isValidUrl(m.shopify_url)) e.shopify_url = "Purchase URL required.";
            if (Object.keys(e).length) merchErrs[m.id] = e;
        });
        if (Object.keys(merchErrs).length) errs.merch = merchErrs;
        return errs;
    };

    const save = async () => {
        if (!canEdit) return;
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) { toast.error("Please fix the highlighted fields"); return; }
        setSaving(true);
        try {
            const clean = (arr) => arr.map(({ id, ...rest }) => (id?.startsWith("tmp_") ? rest : { id, ...rest }));
            const payload = {
                name: form.name.trim(), image_url: form.image_url.trim(), cover_url: form.cover_url.trim(),
                bio: form.bio.trim(), genre: form.genre.trim(),
                additional_genres: form.additional_genres,
                city: form.city.trim(), country: form.country.trim(),
                website: form.website.trim(), shopify_url: form.shopify_url.trim(),
                social_links: clean(form.social_links), music_links: clean(form.music_links),
                albums: form.albums.map((al) => {
                    const { id, ...rest } = al;
                    const base = id?.startsWith("tmp_") ? rest : { id, ...rest };
                    base.type = base.release_type;
                    return base;
                }),
                merch: clean(form.merch).map((m) => ({ ...m, price: typeof m.price === "number" ? m.price : parseFloat(m.price || 0) || 0 })),
            };
            await endpoints.updateArtist(activeArtistId, payload);
            toast.success("Artist page saved");
            load();
            loadStatus();
        } catch { toast.error("Could not save. Try again."); } finally { setSaving(false); }
    };

    const publish = async () => {
        if (publishing) return;
        setPublishing(true);
        try {
            await endpoints.publishArtist(activeArtistId);
            toast.success("Artist page is now live");
            load();
            loadStatus();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.missing)) {
                toast.error(`Cannot publish — missing: ${detail.missing.join(", ")}`);
            } else {
                toast.error("Could not publish. Please save first.");
            }
        } finally { setPublishing(false); }
    };

    const unpublish = async () => {
        if (publishing) return;
        setPublishing(true);
        try {
            await endpoints.unpublishArtist(activeArtistId);
            toast.success("Artist page moved back to draft");
            load();
            loadStatus();
        } catch { toast.error("Could not unpublish"); } finally { setPublishing(false); }
    };

    if (!canEdit) {
        return (
            <div className="p-8 md:p-16" data-testid="artist-editor-forbidden">
                <div className="border border-[#ff3333] p-8 max-w-2xl">
                    <div className="flex items-center gap-2 text-[#ff3333]">
                        <ShieldAlert className="w-5 h-5" />
                        <span className="font-mono uppercase tracking-widest text-xs">Access denied</span>
                    </div>
                    <h1 className="mt-3 font-display font-black uppercase text-2xl md:text-3xl">You don't have permission to edit this page</h1>
                    <p className="mt-3 text-sm text-[#a3a3a3]">Artist Page editing is available to Artist Owners, Team Members and Platform Admins.</p>
                    <Link to="/workspace" className="pm-btn mt-6 inline-flex" data-testid="artist-editor-forbidden-back">Back to workspace</Link>
                </div>
            </div>
        );
    }
    if (loading || !artist) return <div className="p-6 md:p-10 font-mono text-sm text-[#666]" data-testid="artist-editor-loading">Loading editor…</div>;

    return (
        <div className="pm-fade" data-testid="artist-page-editor">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 sticky top-0 bg-[#0a0a0a] z-30">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Artist page</div>
                        <span className="text-[10px] font-mono text-[#333]">·</span>
                        <ReturnToWorkspace testId="artist-editor-return-to-workspace" />
                    </div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Edit public page</h1>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                    {status?.visibility && (
                        <span
                            className={`inline-flex items-center px-2 py-1 text-[10px] font-mono uppercase border ${
                                status.visibility === "public"
                                    ? "text-[#00e676] border-[#00e676]/40"
                                    : status.visibility === "draft"
                                        ? "text-[#ffea00] border-[#ffea00]/40"
                                        : "text-[#a3a3a3] border-[#2a2a2a]"
                            }`}
                            data-testid="artist-editor-visibility"
                        >
                            {status.visibility === "public" ? "Live" : status.visibility}
                        </span>
                    )}
                    <button type="button" onClick={() => navigate(`/artists/${activeArtistId}`)} className="pm-btn" data-testid="artist-editor-preview-btn">
                        <Eye className="w-4 h-4 mr-2" /> Preview public page
                    </button>
                    {status?.visibility === "public" ? (
                        <button
                            type="button"
                            onClick={unpublish}
                            disabled={publishing}
                            className="pm-btn"
                            data-testid="artist-editor-unpublish-btn"
                        >
                            {publishing ? "Working…" : "Move to draft"}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={publish}
                            disabled={publishing || !status?.can_publish}
                            className="pm-btn pm-btn-primary"
                            data-testid="artist-editor-publish-btn"
                            title={status?.can_publish ? "" : "Complete the required fields first"}
                        >
                            {publishing ? "Publishing…" : "Publish artist page"}
                        </button>
                    )}
                    <button type="button" onClick={save} disabled={saving} className="pm-btn" data-testid="artist-editor-save-btn">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </header>

            {status && status.visibility !== "public" && (
                <div className="border-b border-[#2a2a2a] bg-[#141414] px-4 md:px-8 py-3 flex items-start gap-2" data-testid="artist-editor-draft-banner">
                    <Info className="w-4 h-4 text-[#ffea00] mt-0.5 shrink-0" />
                    <div className="text-xs text-[#a3a3a3] leading-relaxed">
                        Your artist page is <strong className="text-[#ffea00]">draft</strong> and not visible publicly.
                        {status.can_publish
                            ? " You've met the minimum profile requirements — publish when you're ready."
                            : " Add profile image, city, country, primary genre, and a short bio to unlock publishing."}
                    </div>
                </div>
            )}

            <div className="px-4 md:px-8 py-6 space-y-8 max-w-5xl">
                <Section title="Basic profile" kicker="01" testId="section-profile">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FieldWrap label="Artist name" error={errors.name}>
                            <TextInput value={form.name} onChange={(v) => set("name", v)} testId="editor-artist-name" error={errors.name} />
                        </FieldWrap>
                        <FieldWrap label="Primary genre" error={errors.genre}>
                            <CatalogSelector
                                mode="single"
                                value={form.genre}
                                onChange={(v) => set("genre", v)}
                                resource="genres"
                                placeholder="Search or add"
                                testId="editor-artist-genre"
                            />
                        </FieldWrap>
                        <FieldWrap label="City" error={errors.city}>
                            <TextInput value={form.city} onChange={(v) => set("city", v)} testId="editor-artist-city" error={errors.city} />
                        </FieldWrap>
                        <FieldWrap label="Country" error={errors.country}>
                            <TextInput value={form.country} onChange={(v) => set("country", v)} testId="editor-artist-country" error={errors.country} />
                        </FieldWrap>
                        <FieldWrap label="Website (optional)" error={errors.website}>
                            <TextInput value={form.website} onChange={(v) => set("website", v)} testId="editor-artist-website" error={errors.website} placeholder="https://…" />
                        </FieldWrap>
                        <FieldWrap label="Shopify store URL (optional)" error={errors.shopify_url}>
                            <TextInput value={form.shopify_url} onChange={(v) => set("shopify_url", v)} testId="editor-artist-shopify" error={errors.shopify_url} placeholder="https://shop.example.com/artist" />
                        </FieldWrap>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FieldWrap label="Profile image">
                            <ImageUploader
                                value={form.image_url}
                                onChange={(v) => set("image_url", v)}
                                scope="artist"
                                artistId={activeArtistId}
                                aspect="square"
                                testId="editor-artist-image"
                            />
                        </FieldWrap>
                        <FieldWrap label="Cover image">
                            <ImageUploader
                                value={form.cover_url}
                                onChange={(v) => set("cover_url", v)}
                                scope="artist"
                                artistId={activeArtistId}
                                aspect="video"
                                testId="editor-artist-cover"
                            />
                        </FieldWrap>
                    </div>

                    <FieldWrap label="Additional genres (optional)">
                        <CatalogSelector
                            mode="multi"
                            value={form.additional_genres}
                            onChange={(v) => set("additional_genres", v)}
                            resource="genres"
                            disabledValues={[form.genre].filter(Boolean)}
                            placeholder="Search or add"
                            testId="editor-artist-additional-genres"
                        />
                    </FieldWrap>

                    <FieldWrap label="Biography" error={errors.bio}>
                        <textarea rows={5} value={form.bio} onChange={(e) => set("bio", e.target.value)}
                            className={`w-full bg-transparent border px-3 py-2 outline-none ${errors.bio ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                            data-testid="editor-artist-bio" aria-invalid={!!errors.bio} />
                    </FieldWrap>

                    <NoteBar>Upload images directly from your phone or computer. Existing URL-based images continue to work.</NoteBar>
                </Section>

                <Section title="Social links" kicker="02" testId="section-social">
                    <ItemList items={form.social_links} addLabel="Add social link"
                        onAdd={() => listAdd("social_links", { platform: "Instagram", url: "" })}
                        onMove={(id, dir) => listMove("social_links", id, dir)}
                        onRemove={(id) => listRemove("social_links", id)}
                        emptyText="No social links yet." testIdPrefix="social"
                        renderItem={(item, i) => {
                            const itemErr = (errors.social_links || {})[item.id] || {};
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
                                    <select value={item.platform} onChange={(e) => listUpdate("social_links", item.id, { platform: e.target.value })}
                                        className={`bg-transparent border px-3 py-2 font-mono uppercase text-sm outline-none ${itemErr.platform ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                                        data-testid={`social-platform-${i}`}>
                                        {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}</option>)}
                                    </select>
                                    <div>
                                        <TextInput value={item.url} onChange={(v) => listUpdate("social_links", item.id, { url: v })}
                                            placeholder="https://instagram.com/handle" testId={`social-url-${i}`} error={itemErr.url} />
                                        {itemErr.url && <ErrText>{itemErr.url}</ErrText>}
                                    </div>
                                </div>
                            );
                        }} />
                </Section>

                <Section title="Music links" kicker="03" testId="section-music">
                    <ItemList items={form.music_links} addLabel="Add music link"
                        onAdd={() => listAdd("music_links", { platform: "Spotify", url: "" })}
                        onMove={(id, dir) => listMove("music_links", id, dir)}
                        onRemove={(id) => listRemove("music_links", id)}
                        emptyText="No music links yet." testIdPrefix="music"
                        renderItem={(item, i) => {
                            const itemErr = (errors.music_links || {})[item.id] || {};
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
                                    <select value={item.platform} onChange={(e) => listUpdate("music_links", item.id, { platform: e.target.value })}
                                        className={`bg-transparent border px-3 py-2 font-mono uppercase text-sm outline-none ${itemErr.platform ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                                        data-testid={`music-platform-${i}`}>
                                        {MUSIC_PLATFORMS.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}</option>)}
                                    </select>
                                    <div>
                                        <TextInput value={item.url} onChange={(v) => listUpdate("music_links", item.id, { url: v })}
                                            placeholder="https://open.spotify.com/artist/…" testId={`music-url-${i}`} error={itemErr.url} />
                                        {itemErr.url && <ErrText>{itemErr.url}</ErrText>}
                                    </div>
                                </div>
                            );
                        }} />
                </Section>

                <Section title="Releases" kicker="04" testId="section-releases">
                    <ItemList items={form.albums} addLabel="Add release"
                        onAdd={() => listAdd("albums", { title: "", release_type: "album", year: new Date().getFullYear(), cover_url: "", external_url: "", description: "" })}
                        onMove={(id, dir) => listMove("albums", id, dir)}
                        onRemove={(id) => listRemove("albums", id)}
                        emptyText="No releases yet." testIdPrefix="release"
                        renderItem={(item, i) => {
                            const itemErr = (errors.albums || {})[item.id] || {};
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
                                    <ImageUploader
                                        value={item.cover_url}
                                        onChange={(v) => listUpdate("albums", item.id, { cover_url: v })}
                                        scope="artist"
                                        artistId={activeArtistId}
                                        aspect="square"
                                        testId={`release-cover-${i}`}
                                    />
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-3">
                                            <div>
                                                <TextInput value={item.title} onChange={(v) => listUpdate("albums", item.id, { title: v })}
                                                    placeholder="Title" testId={`release-title-${i}`} error={itemErr.title} />
                                                {itemErr.title && <ErrText>{itemErr.title}</ErrText>}
                                            </div>
                                            <select value={item.release_type || "album"} onChange={(e) => listUpdate("albums", item.id, { release_type: e.target.value })}
                                                className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-sm focus:border-[#ff4400] outline-none"
                                                data-testid={`release-type-${i}`}>
                                                {RELEASE_TYPES.map((r) => <option key={r} value={r} className="bg-[#141414]">{r}</option>)}
                                            </select>
                                            <input type="number" value={item.year || ""} onChange={(e) => listUpdate("albums", item.id, { year: parseInt(e.target.value || 0) })}
                                                placeholder="Year" className="bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none"
                                                data-testid={`release-year-${i}`} />
                                        </div>
                                        <div>
                                            <TextInput value={item.external_url || ""} onChange={(v) => listUpdate("albums", item.id, { external_url: v })}
                                                placeholder="External listening link (Spotify, Bandcamp, …)" testId={`release-external-${i}`} error={itemErr.external_url} />
                                            {itemErr.external_url && <ErrText>{itemErr.external_url}</ErrText>}
                                        </div>
                                        <textarea rows={2} value={item.description || ""} onChange={(e) => listUpdate("albums", item.id, { description: e.target.value })}
                                            placeholder="Optional description"
                                            className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none text-sm"
                                            data-testid={`release-desc-${i}`} />
                                    </div>
                                </div>
                            );
                        }} />
                </Section>

                <Section title="Merchandise" kicker="05" testId="section-merch">
                    <ItemList items={form.merch} addLabel="Add merch item"
                        onAdd={() => listAdd("merch", { title: "", product_type: "Apparel", price: 0, image_url: "", shopify_url: "", active: true })}
                        onMove={(id, dir) => listMove("merch", id, dir)}
                        onRemove={(id) => listRemove("merch", id)}
                        emptyText="No merch yet." testIdPrefix="merch"
                        renderItem={(item, i) => {
                            const itemErr = (errors.merch || {})[item.id] || {};
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
                                    <ImageUploader
                                        value={item.image_url}
                                        onChange={(v) => listUpdate("merch", item.id, { image_url: v })}
                                        scope="artist"
                                        artistId={activeArtistId}
                                        aspect="square"
                                        testId={`merch-image-${i}`}
                                    />
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr] gap-3">
                                            <div>
                                                <TextInput value={item.title} onChange={(v) => listUpdate("merch", item.id, { title: v })}
                                                    placeholder="Product name" testId={`merch-title-${i}`} error={itemErr.title} />
                                                {itemErr.title && <ErrText>{itemErr.title}</ErrText>}
                                            </div>
                                            <select value={item.product_type} onChange={(e) => listUpdate("merch", item.id, { product_type: e.target.value })}
                                                className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-sm focus:border-[#ff4400] outline-none"
                                                data-testid={`merch-type-${i}`}>
                                                {MERCH_TYPES.map((t) => <option key={t} value={t} className="bg-[#141414]">{t}</option>)}
                                            </select>
                                            <input type="number" step="0.01" value={item.price || ""} onChange={(e) => listUpdate("merch", item.id, { price: parseFloat(e.target.value || 0) })}
                                                placeholder="Display price" className="bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none"
                                                data-testid={`merch-price-${i}`} />
                                        </div>
                                        <div>
                                            <TextInput value={item.shopify_url} onChange={(v) => listUpdate("merch", item.id, { shopify_url: v })}
                                                placeholder="External purchase URL (Shopify)" testId={`merch-url-${i}`} error={itemErr.shopify_url} />
                                            {itemErr.shopify_url && <ErrText>{itemErr.shopify_url}</ErrText>}
                                        </div>
                                        <label className="flex items-center gap-2 cursor-pointer" data-testid={`merch-active-${i}`}>
                                            <input type="checkbox" checked={item.active} onChange={(e) => listUpdate("merch", item.id, { active: e.target.checked })} className="accent-[#ff4400]" />
                                            <span className="text-xs font-mono uppercase">{item.active ? "Active — shown on public page" : "Inactive — hidden"}</span>
                                        </label>
                                    </div>
                                </div>
                            );
                        }} />
                </Section>

                <Section title="Events" kicker="06" testId="section-events">
                    <div className="border border-[#2a2a2a] p-4 flex items-start gap-3" data-testid="events-readonly-note">
                        <Info className="w-5 h-5 text-[#ff4400] shrink-0 mt-0.5" />
                        <div>
                            <div className="font-display font-bold uppercase text-sm">Events aren't edited here</div>
                            <p className="mt-1 text-sm text-[#a3a3a3]">Manage events in the <Link to="/workspace/events" className="text-[#ff4400] hover:underline mx-1" data-testid="events-workspace-link">Events workspace</Link>.</p>
                        </div>
                    </div>
                </Section>

                <div className="border-t border-[#2a2a2a] pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 sticky bottom-0 bg-[#0a0a0a] py-4">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Changes go live immediately once saved.</div>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => navigate(`/artists/${activeArtistId}`)} className="pm-btn" data-testid="artist-editor-preview-btn-bottom">
                            <Eye className="w-4 h-4 mr-2" /> Preview
                        </button>
                        <button type="button" onClick={save} disabled={saving} className="pm-btn pm-btn-primary" data-testid="artist-editor-save-btn-bottom">
                            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, kicker, testId, children }) {
    return (
        <section className="space-y-4" data-testid={testId}>
            <div className="flex items-baseline gap-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{kicker}</div>
                <h2 className="font-display font-black uppercase tracking-tighter text-xl md:text-2xl">{title}</h2>
            </div>
            {children}
        </section>
    );
}
function FieldWrap({ label, error, children }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">{label}</div>
            {children}
            {error && typeof error === "string" && <ErrText>{error}</ErrText>}
        </div>
    );
}
function TextInput({ value, onChange, placeholder, testId, error }) {
    return (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            data-testid={testId} aria-invalid={!!error}
            className={`w-full bg-transparent border px-3 py-2 outline-none text-sm ${error ? "border-[#ff3333] focus:border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`} />
    );
}
function ErrText({ children }) { return <div className="mt-1 text-xs font-mono text-[#ff3333]">{children}</div>; }
function NoteBar({ children }) {
    return (
        <div className="border border-[#2a2a2a] px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3] flex items-center gap-2">
            <Info className="w-3 h-3" /> {children}
        </div>
    );
}
function ItemList({ items, onAdd, onMove, onRemove, addLabel, emptyText, renderItem, testIdPrefix }) {
    return (
        <div className="space-y-3">
            {items.length === 0 && (
                <div className="border border-dashed border-[#2a2a2a] p-6 text-center text-[#a3a3a3] text-sm" data-testid={`${testIdPrefix}-empty`}>{emptyText}</div>
            )}
            {items.map((it, i) => (
                <div key={it.id} className="border border-[#2a2a2a] p-4" data-testid={`${testIdPrefix}-item-${i}`}>
                    {renderItem(it, i)}
                    <div className="mt-3 flex items-center justify-end gap-1 border-t border-[#2a2a2a] pt-3">
                        <button type="button" onClick={() => onMove(it.id, -1)} disabled={i === 0} className="pm-btn text-xs disabled:opacity-40" data-testid={`${testIdPrefix}-up-${i}`} aria-label="Move up">
                            <ArrowUp className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => onMove(it.id, 1)} disabled={i === items.length - 1} className="pm-btn text-xs disabled:opacity-40" data-testid={`${testIdPrefix}-down-${i}`} aria-label="Move down">
                            <ArrowDown className="w-3 h-3" />
                        </button>
                        <button type="button" onClick={() => onRemove(it.id)} className="pm-btn text-xs text-[#ff3333] border-[#ff3333]" data-testid={`${testIdPrefix}-remove-${i}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Remove
                        </button>
                    </div>
                </div>
            ))}
            <button type="button" onClick={onAdd} className="pm-btn" data-testid={`${testIdPrefix}-add`}>
                <Plus className="w-4 h-4 mr-2" /> {addLabel}
            </button>
        </div>
    );
}
